import { clampNumber, roundNumber } from "./utils.js";

export const scenePackageVersion = "kapowx.scene-package.phase0-authoring.v2";
export const gltfAssetBasis = {
	coordinateSystem: "right-handed",
	units: "meters",
	upAxis: "+Y",
	forwardAxis: "+Z",
	rightAxis: "+X",
};
export const kapowxSemanticMetadata = {
	transport: "glTF extras",
	semanticNamesAreReadableHints: true,
	semanticNamesRequireExtrasFallback: true,
};
export const tableReferenceMeters = {
	width: 1.2,
	height: 0.75,
	depth: 0.8,
};
export const comicThicknessMeters = 0.0032;
export const comicFormatMetricsMeters = {
	"comic-golden": {
		width: 0.1966,
		height: 0.2667,
		thickness: comicThicknessMeters,
	},
	"comic-silver": {
		width: 0.181,
		height: 0.2667,
		thickness: comicThicknessMeters,
	},
	"comic-bronze": {
		width: 0.1842,
		height: 0.2667,
		thickness: comicThicknessMeters,
	},
	"comic-modern": {
		width: 0.1683,
		height: 0.2604,
		thickness: comicThicknessMeters,
	},
};
export const boxedComicReferenceMeters = {
	...comicFormatMetricsMeters["comic-modern"],
};
export const STATIC_SLOTS = [
	{ id: "wall-comic-shelf",   kind: "wall-shelf",  label: "East Wall (Comic Shelf)", accepts: "comic" },
	{ id: "comic-pile",         kind: "comic-pile",  label: "Table Comic Pile",        accepts: "comic" },
	{ id: "south-book-shelf-0", kind: "book-shelf",  label: "Book Shelf — Bottom",     accepts: "book"  },
	{ id: "south-book-shelf-1", kind: "book-shelf",  label: "Book Shelf — Middle",     accepts: "book"  },
	{ id: "south-book-shelf-2", kind: "book-shelf",  label: "Book Shelf — Top",        accepts: "book"  },
];

export const comicMaterialSlotKeys = ["front", "back", "spine", "pages"];
export const defaultComicMaterialSlots = {
	front: {
		source: "product",
		projectMode: "texture",
		color: "#79aee8",
		textureAssetId: "",
		textureUrl: "",
	},
	back: {
		source: "product",
		projectMode: "texture",
		color: "#8eb5ea",
		textureAssetId: "",
		textureUrl: "",
	},
	spine: {
		source: "project",
		projectMode: "color",
		color: "#4f86c6",
		textureAssetId: "",
		textureUrl: "",
	},
	pages: {
		source: "project",
		projectMode: "color",
		color: "#f4eee3",
		textureAssetId: "",
		textureUrl: "",
	},
};
export const defaultComicInstanceMaterialOverrides = {
	enabled: false,
	slots: Object.fromEntries(
		comicMaterialSlotKeys.map((slotKey) => [
			slotKey,
			{
				mode: "default",
				color: defaultComicMaterialSlots[slotKey].color,
			},
		]),
	),
};
export const defaultComicVariantKey = "comic-modern";
export const comicAssetValidationVersion = "kapowx.comic-asset-validation.v1";
export const comicPrefabVariants = [
	{
		key: "comic-modern",
		label: "Modern",
		era: "Modern Age",
		source: "Core",
		kind: "Prefab Variant",
		exportPolicy: "Creator Kit Allowed",
		runtimeAssetUrl: "",
		creatorKitAssetUrl: "",
		assetValidation: createDefaultComicAssetValidation(),
		referenceComicMetricsMeters: { ...comicFormatMetricsMeters["comic-modern"] },
	},
	{
		key: "comic-bronze",
		label: "Bronze",
		era: "Bronze Age",
		source: "Core",
		kind: "Prefab Variant",
		exportPolicy: "Creator Kit Allowed",
		runtimeAssetUrl: "",
		creatorKitAssetUrl: "",
		assetValidation: createDefaultComicAssetValidation(),
		referenceComicMetricsMeters: { ...comicFormatMetricsMeters["comic-bronze"] },
	},
	{
		key: "comic-silver",
		label: "Silver",
		era: "Silver Age",
		source: "Core",
		kind: "Prefab Variant",
		exportPolicy: "Creator Kit Allowed",
		runtimeAssetUrl: "",
		creatorKitAssetUrl: "",
		assetValidation: createDefaultComicAssetValidation(),
		referenceComicMetricsMeters: { ...comicFormatMetricsMeters["comic-silver"] },
	},
	{
		key: "comic-golden",
		label: "Golden",
		era: "Golden Age",
		source: "Core",
		kind: "Prefab Variant",
		exportPolicy: "Creator Kit Allowed",
		runtimeAssetUrl: "",
		creatorKitAssetUrl: "",
		assetValidation: createDefaultComicAssetValidation(),
		referenceComicMetricsMeters: { ...comicFormatMetricsMeters["comic-golden"] },
	},
];

export function createDefaultComicAssetValidation() {
	return {
		version: comicAssetValidationVersion,
		status: "not-checked",
		checkedAt: "",
		assetRole: "runtime",
		summary: "Not checked",
		errors: [],
		warnings: [],
		details: [],
	};
}

// ─── Surface material schema ──────────────────────────────────────────────────

export const roomSurfaceSlotKeys = ["north", "south", "east", "west", "floor", "ceiling"];
export const tableSurfaceSlotKeys = ["topSides", "legsBase"];
export const longboxSurfaceSlotKeys = ["body", "lid"];

export const roomSurfaceLabels = {
	north: "Back Wall",
	south: "Front Wall",
	east: "Right Wall",
	west: "Left Wall",
	floor: "Floor",
	ceiling: "Ceiling",
};
export const tableSurfaceLabels = { topSides: "Top & Sides", legsBase: "Legs & Base" };
export const longboxSurfaceLabels = { body: "Body", lid: "Lid" };

const defaultRoomSurfaceColors = {
	north: "#bca58c",
	south: "#b5a28a",
	east: "#aa9076",
	west: "#aa9076",
	floor: "#dfc7ab",
	ceiling: "#c8b89a",
};
const defaultTableSurfaceColors = { topSides: "#815a37", legsBase: "#604223" };
const defaultLongboxSurfaceColors = { body: "#c6a16d", lid: "#c89b67" };

export function createDefaultSurfaceMaterial(color = "#ffffff") {
	return { mode: "color", color, textureUrl: "", visible: true };
}

export function createDefaultRoomSurfaces() {
	return Object.fromEntries(
		roomSurfaceSlotKeys.map((key) => [key, createDefaultSurfaceMaterial(defaultRoomSurfaceColors[key])]),
	);
}

export function createDefaultTableSurfaces() {
	return Object.fromEntries(
		tableSurfaceSlotKeys.map((key) => [key, createDefaultSurfaceMaterial(defaultTableSurfaceColors[key])]),
	);
}

export function createDefaultLongboxSurfaces() {
	return Object.fromEntries(
		longboxSurfaceSlotKeys.map((key) => [key, createDefaultSurfaceMaterial(defaultLongboxSurfaceColors[key])]),
	);
}

export function getDefaultFixtureSurfaces(templateKey) {
	const template = getTemplateByKey(templateKey);
	if (template.kind === "table") return createDefaultTableSurfaces();
	if (template.kind === "longbox") return createDefaultLongboxSurfaces();
	return null;
}

export function hydrateRoomSurfaces(candidate) {
	const defaults = createDefaultRoomSurfaces();
	return Object.fromEntries(
		roomSurfaceSlotKeys.map((key) => [key, hydrateSurfaceMaterial(candidate?.[key], defaults[key])]),
	);
}

export function hydrateFixtureSurfaces(candidate, templateKey) {
	const template = getTemplateByKey(templateKey);
	if (template.kind === "table") {
		const defaults = createDefaultTableSurfaces();
		return Object.fromEntries(
			tableSurfaceSlotKeys.map((key) => [key, hydrateSurfaceMaterial(candidate?.[key], defaults[key])]),
		);
	}
	if (template.kind === "longbox") {
		const defaults = createDefaultLongboxSurfaces();
		return Object.fromEntries(
			longboxSurfaceSlotKeys.map((key) => [key, hydrateSurfaceMaterial(candidate?.[key], defaults[key])]),
		);
	}
	return null;
}

function hydrateSurfaceMaterial(candidate, fallback) {
	const mode = String(candidate?.mode || fallback?.mode || "color");
	return {
		mode: ["color", "url"].includes(mode) ? mode : "color",
		color: normalizeHexColor(candidate?.color ?? fallback?.color ?? "#ffffff"),
		textureUrl: String(candidate?.textureUrl || "").trim(),
		visible: Boolean(candidate?.visible ?? fallback?.visible ?? true),
	};
}

export function createDefaultProjectSettings() {
	return {
		projectTextureAssets: [],
		comicMaterialSlots: { ...defaultComicMaterialSlots },
		comicPrefabVariantOverrides: {},
		comicPrefabVariants: [],
		longboxPrefabVariantOverrides: {},
		longboxPrefabVariants: [],
	};
}

export function hydrateProjectSettings(candidate) {
	const defaults = createDefaultProjectSettings();
	const importedVariants = Array.isArray(candidate?.comicPrefabVariants)
		? candidate.comicPrefabVariants
		: defaults.comicPrefabVariants;
	const importedLongboxVariants = Array.isArray(candidate?.longboxPrefabVariants)
		? candidate.longboxPrefabVariants
		: defaults.longboxPrefabVariants;

	return {
		projectTextureAssets: hydrateProjectTextureAssets(candidate?.projectTextureAssets),
		comicMaterialSlots: hydrateComicMaterialSlots(candidate?.comicMaterialSlots),
		comicPrefabVariantOverrides: hydrateVariantOverrides(candidate?.comicPrefabVariantOverrides),
		comicPrefabVariants: importedVariants
			.map((variant, index) => hydrateProjectComicVariant(variant, index))
			.filter(Boolean),
		longboxPrefabVariantOverrides: hydrateLongboxVariantOverrides(candidate?.longboxPrefabVariantOverrides),
		longboxPrefabVariants: importedLongboxVariants
			.map((variant, index) => hydrateProjectLongboxVariant(variant, index))
			.filter(Boolean),
	};
}

export function getAvailableComicPrefabVariants(projectSettings = null) {
	const imported = Array.isArray(projectSettings?.comicPrefabVariants)
		? projectSettings.comicPrefabVariants
		: [];
	const overrides = projectSettings?.comicPrefabVariantOverrides || {};
	const core = comicPrefabVariants.map((variant) => applyComicVariantOverride(variant, overrides[variant.key]));

	return [...core, ...imported.map((variant) => applyComicVariantOverride(variant, overrides[variant.key]))];
}

export function getAvailableLongboxPrefabVariants(projectSettings = null) {
	const imported = Array.isArray(projectSettings?.longboxPrefabVariants)
		? projectSettings.longboxPrefabVariants
		: [];
	const overrides = projectSettings?.longboxPrefabVariantOverrides || {};
	const core = longboxPrefabVariants.map((variant) => applyLongboxVariantOverride(variant, overrides[variant.key]));

	return [...core, ...imported.map((variant) => applyLongboxVariantOverride(variant, overrides[variant.key]))];
}

export function resolveComicMaterialSlots(projectSettings = null, variantKey = defaultComicVariantKey) {
	const settings = hydrateProjectSettings(projectSettings);
	const variant = getAvailableComicPrefabVariants(settings).find((item) => item.key === variantKey);
	const slots = {
		...settings.comicMaterialSlots,
		...(variant?.materialOverrides || {}),
	};

	return Object.fromEntries(
		Object.entries(slots).map(([slotKey, slot]) => [
			slotKey,
			resolveProjectTextureForSlot(slot, settings.projectTextureAssets),
		]),
	);
}

export function resolveComicFixtureMaterialSlots(projectSettings = null, fixture = null) {
	const variantKey = fixture?.variantKey || fixture?.contract?.variantKey || defaultComicVariantKey;
	const baseSlots = resolveComicMaterialSlots(projectSettings, variantKey);
	const overrides = hydrateComicInstanceMaterialOverrides(fixture?.instanceMaterialOverrides);

	if (!overrides.enabled) {
		return baseSlots;
	}

	return Object.fromEntries(
		comicMaterialSlotKeys.map((slotKey) => {
			const baseSlot = baseSlots[slotKey] || defaultComicMaterialSlots[slotKey];
			const override = overrides.slots[slotKey];

			if (override.mode === "product") {
				return [slotKey, { ...baseSlot, source: "product" }];
			}

			if (override.mode === "color") {
				return [
					slotKey,
					{
						...baseSlot,
						source: "project",
						projectMode: "color",
						color: override.color,
						textureAssetId: "",
						textureUrl: "",
					},
				];
			}

			return [slotKey, baseSlot];
		}),
	);
}

export const longboxReferenceMeters = {
	width: 0.71,
	height: 0.305,
	depth: 0.215,
};
export const shortboxReferenceMeters = {
	width: 0.355,
	height: 0.305,
	depth: 0.215,
};
export const longboxAssetValidationVersion = "kapowx.longbox-asset-validation.v1";
export const defaultLongboxVariantKey = "longbox-core";
export const longboxPrefabVariants = [
	{
		key: defaultLongboxVariantKey,
		label: "Core Longbox",
		source: "Core",
		kind: "Prefab Variant",
		exportPolicy: "Creator Kit Allowed",
		runtimeAssetUrl: "",
		assetUrl: "",
		assetValidation: createDefaultLongboxAssetValidation(),
		referenceLongboxMetricsMeters: { ...longboxReferenceMeters },
	},
];
export const requiredLongboxNodes = [
	"lane_origin",
	"lane_bounds",
	"slot_001_anchor",
	"slot_002_anchor",
	"hero_extract_anchor",
	"lid_root",
];
export const optionalLongboxNodes = ["label_surface", "slider_side_anchor", "camera_focus_anchor"];
export const requiredComicNodes = ["comic_root", "front_surface", "back_surface", "spine_surface", "pages_block"];
export const optionalComicNodes = ["inside_front_cover", "inside_back_cover"];

export function createDefaultLongboxAssetValidation() {
	return {
		version: longboxAssetValidationVersion,
		status: "not-checked",
		checkedAt: "",
		assetRole: "runtime",
		summary: "Not checked",
		errors: [],
		warnings: [],
		details: [],
	};
}

export const proxyLibrary = [
	{
		key: "display-table",
		label: "Display Table",
		group: "Prefabs",
		kind: "table",
		category: "support",
		color: "#8a5f39",
		materialOptions: ["Walnut Display", "Painted Pine", "Industrial Steel"],
		defaults: {
			position: { x: 0, y: 0, z: 0 },
			rotation: { x: 0, y: 0, z: 0 },
			scale: { x: tableReferenceMeters.width, y: tableReferenceMeters.height, z: tableReferenceMeters.depth },
			material: "Walnut Display",
		},
	},
	{
		key: "boxed-comic-proxy",
		label: "Comic",
		group: "Prefabs",
		kind: "boxed-comic",
		category: "storage",
		color: "#6f96cd",
		prefabKey: "comic",
		defaultVariantKey: defaultComicVariantKey,
		materialOptions: ["White Stock", "Neutral Preview"],
		defaults: {
			position: { x: 0, y: 0, z: 0 },
			rotation: { x: 0, y: 0, z: 0 },
			scale: { x: boxedComicReferenceMeters.width, y: boxedComicReferenceMeters.height, z: boxedComicReferenceMeters.thickness },
			material: "White Stock",
		},
		comicDefaults: {
			requiredComicNodes: [...requiredComicNodes],
			optionalComicNodes: [...optionalComicNodes],
			referenceComicMetricsMeters: { ...boxedComicReferenceMeters },
		},
	},
	{
		key: "long-comic-box",
		label: "Long Comic Box",
		group: "Prefabs",
		kind: "longbox",
		category: "storage",
		color: "#c6a16d",
		prefabKey: "longbox",
		defaultVariantKey: defaultLongboxVariantKey,
		materialOptions: ["Raw Cardboard", "Label-Ready Kraft", "Premium Print Sample"],
		defaults: {
			position: { x: 0, y: 0, z: 0 },
			rotation: { x: 0, y: 0, z: 0 },
			scale: { x: longboxReferenceMeters.width, y: longboxReferenceMeters.height, z: longboxReferenceMeters.depth },
			material: "Raw Cardboard",
		},
		storageDefaults: {
			slotCount: 12,
			sequenceStartProductId: null,
			assignments: [],
			logicalCapacity: 100,
			referenceComicFormat: "modern-standard",
			requiredAnchors: [...requiredLongboxNodes],
			optionalAnchors: [...optionalLongboxNodes],
			laneDirectionSource: "slot-anchor-delta",
			comicAssetKind: "comic-boxed",
			fixtureAssetKind: "long-box",
			requiredComicNodes: [...requiredComicNodes],
			optionalComicNodes: [...optionalComicNodes],
			referenceComicMetricsMeters: { ...boxedComicReferenceMeters },
			proxyReferenceMeters: { ...longboxReferenceMeters },
		},
	},
	{
		key: "short-comic-box",
		label: "Short Comic Box",
		group: "Prefabs",
		kind: "longbox",
		category: "storage",
		color: "#c6a16d",
		prefabKey: "shortbox",
		defaultVariantKey: defaultLongboxVariantKey,
		materialOptions: ["Raw Cardboard", "Label-Ready Kraft", "Premium Print Sample"],
		defaults: {
			position: { x: 0, y: 0, z: 0 },
			rotation: { x: 0, y: 0, z: 0 },
			scale: { x: shortboxReferenceMeters.width, y: shortboxReferenceMeters.height, z: shortboxReferenceMeters.depth },
			material: "Raw Cardboard",
		},
		storageDefaults: {
			slotCount: 46,
			sequenceStartProductId: null,
			assignments: [],
			logicalCapacity: 46,
			referenceComicFormat: "modern-standard",
			requiredAnchors: [...requiredLongboxNodes],
			optionalAnchors: [...optionalLongboxNodes],
			laneDirectionSource: "slot-anchor-delta",
			comicAssetKind: "comic-boxed",
			fixtureAssetKind: "short-box",
			requiredComicNodes: [...requiredComicNodes],
			optionalComicNodes: [...optionalComicNodes],
			referenceComicMetricsMeters: { ...boxedComicReferenceMeters },
			proxyReferenceMeters: { ...shortboxReferenceMeters },
		},
	},
];

export function getTemplateByKey(key) {
	return proxyLibrary.find((template) => template.key === key) ?? proxyLibrary[0];
}

export function getComicPrefabVariant(key) {
	return comicPrefabVariants.find((item) => item.key === key) ?? comicPrefabVariants[0];
}

export function getLongboxPrefabVariant(key) {
	return longboxPrefabVariants.find((item) => item.key === key) ?? longboxPrefabVariants[0];
}

export function resolveComicPrefabVariant(sceneOrSettings, key) {
	const projectSettings = sceneOrSettings?.projectSettings || sceneOrSettings;
	return getAvailableComicPrefabVariants(projectSettings).find((item) => item.key === key) ?? comicPrefabVariants[0];
}

export function resolveLongboxPrefabVariant(sceneOrSettings, key) {
	const projectSettings = sceneOrSettings?.projectSettings || sceneOrSettings;
	return getAvailableLongboxPrefabVariants(projectSettings).find((item) => item.key === key) ?? longboxPrefabVariants[0];
}

export function validateComicRuntimeAsset(arrayBuffer, expectedMetrics = boxedComicReferenceMeters) {
	const checkedAt = new Date().toISOString();

	try {
		const gltf = parseGlbJson(arrayBuffer);
		const report = inspectComicAssetJson(gltf, {
			assetRole: "runtime",
			expectedMetrics,
			checkedAt,
		});

		return {
			...report,
			version: comicAssetValidationVersion,
			checkedAt,
		};
	} catch (error) {
		return {
			...createDefaultComicAssetValidation(),
			status: "error",
			checkedAt,
			summary: "Unable to read GLB",
			errors: [error?.message || "The asset could not be parsed as a GLB."],
		};
	}
}

export function validateLongboxRuntimeAsset(arrayBuffer) {
	const checkedAt = new Date().toISOString();

	try {
		const gltf = parseGlbJson(arrayBuffer);
		const report = inspectLongboxAssetJson(gltf, {
			assetRole: "runtime",
			checkedAt,
		});

		return {
			...report,
			version: longboxAssetValidationVersion,
			checkedAt,
		};
	} catch (error) {
		return {
			...createDefaultLongboxAssetValidation(),
			status: "error",
			checkedAt,
			summary: "Unable to read GLB",
			errors: [error?.message || "The asset could not be parsed as a GLB."],
		};
	}
}

export function createDefaultScene(title = "KapowX Authoring Studio") {
	return {
		version: scenePackageVersion,
		metadata: {
			sceneId: createSceneId(),
			title,
			mode: "authoring",
			focus: "comic-longbox-phase0",
		},
		room: {
			width: 4,
			depth: 3,
			height: 3,
			surfaces: createDefaultRoomSurfaces(),
		},
		projectSettings: createDefaultProjectSettings(),
		runtimeBindings: createDefaultRuntimeBindings(),
		shopGeometryBinding: null,
		fixtures: [],
		staticSlots: STATIC_SLOTS,
	};
}

// Optional shell-GLB binding for the shop scene. When set, the runtime loads
// the GLB via projectAssets[assetKey].assetUrl and harvests anchor empties
// (LONGBOX_ANCHOR_NN, WALL_SHELF_ANCHOR_NN, etc.) so fixtures can be authored
// in Blender instead of placed procedurally. `null` disables the feature.
export function hydrateShopGeometryBinding(candidate) {
	if (!candidate || typeof candidate !== "object") return null;
	const assetKey = typeof candidate.assetKey === "string" ? candidate.assetKey.trim() : "";
	if (!assetKey) return null;
	const scale = Number.isFinite(candidate.scale) ? candidate.scale : 1.0;
	const out = { assetKey, scale };
	if (candidate.hideUpperFloor === true) out.hideUpperFloor = true;
	if (candidate.hideCeiling === true)    out.hideCeiling = true;
	if (candidate.flatShade === false)     out.flatShade = false; // default true; allow opt-out
	return out;
}

export function createKapowUndergroundScene({ title = "KapowX Authoring Studio", pluginUrl = "", longboxAssetUrl = "", comicAssetUrl = "" } = {}) {
	const tablePos = { x: 0, y: 0, z: -0.3 };
	const tableScale = { x: 1.2, y: 0.75, z: 0.8 };
	const boxY = tablePos.y + tableScale.y + 0.04;
	const boxDims = { ...longboxReferenceMeters };
	const proxyGuideMeters = createDefaultLongboxProxyGuideMeters(boxDims);
	const wallUrl = pluginUrl ? `${pluginUrl}textures/room-back-wall.jpg` : "";
	const floorUrl = pluginUrl ? `${pluginUrl}textures/room-floor.jpg` : "";
	const surfaceMode = (url) => (url ? "url" : "color");

	return {
		version: scenePackageVersion,
		metadata: {
			sceneId: createSceneId(),
			title,
			mode: "authoring",
			focus: "comic-longbox-phase0",
		},
		room: {
			width: 3,
			depth: 3,
			height: 2,
			surfaces: {
				north:   { mode: surfaceMode(wallUrl),  color: "#8a7460", textureUrl: wallUrl,  visible: true },
				south:   { mode: surfaceMode(wallUrl),  color: "#6a5848", textureUrl: wallUrl,  visible: true },
				east:    { mode: surfaceMode(wallUrl),  color: "#7a6450", textureUrl: wallUrl,  visible: true },
				west:    { mode: surfaceMode(wallUrl),  color: "#7a6450", textureUrl: wallUrl,  visible: true },
				floor:   { mode: surfaceMode(floorUrl), color: "#dfc7ab", textureUrl: floorUrl, visible: true },
				ceiling: { mode: "color",               color: "#18100c", textureUrl: "",       visible: true },
			},
		},
		projectSettings: createDefaultProjectSettings(),
		runtimeBindings: {
			sharedComic: { assetUrl: comicAssetUrl, assetFormat: "glb" },
		},
		fixtures: [
			{
				id: `display-table-${Date.now().toString(36)}-1`,
				templateKey: "display-table",
				variantKey: null,
				name: "Display Table",
				kind: "table",
				material: "Walnut Display",
				contract: { kind: "table", anchor: "floor", behaviorClass: "support" },
				assetBinding: null,
				instanceMaterialOverrides: null,
				surfaceMaterials: {
					topSides: { mode: "color", color: "#7c5230", textureUrl: "", visible: true },
					legsBase: { mode: "color", color: "#5a3a1a", textureUrl: "", visible: true },
				},
				transform: {
					position: { ...tablePos },
					rotation: { x: 0, y: 0, z: 0 },
					scale: { ...tableScale },
				},
				storage: null,
			},
			{
				id: `long-comic-box-${Date.now().toString(36)}-1`,
				templateKey: "long-comic-box",
				variantKey: "longbox-core",
				name: "Long Comic Box",
				kind: "longbox",
				material: "Raw Cardboard",
				contract: { kind: "long-box", prefabKey: "longbox", variantKey: "longbox-core", laneDirectionSource: "slot-anchor-delta" },
				assetBinding: { assetUrl: longboxAssetUrl, assetFormat: "glb", productId: null },
				instanceMaterialOverrides: null,
				surfaceMaterials: {
					body: { mode: "color", color: "#c6a16d", textureUrl: "", visible: true },
					lid:  { mode: "color", color: "#c89b67", textureUrl: "", visible: true },
				},
				transform: {
					position: { x: tablePos.x, y: boxY, z: tablePos.z },
					rotation: { x: 0, y: 0, z: 0 },
					scale: { x: boxDims.width, y: boxDims.height, z: boxDims.depth },
				},
				storage: {
					slotCount: 93,
					logicalCapacity: 93,
					cycleProducts: true,
					sequenceStartProductId: null,
					assignments: [],
					laneDirectionSource: "slot-anchor-delta",
					comicAssetKind: "comic-boxed",
					fixtureAssetKind: "long-box",
					referenceComicFormat: "modern-standard",
					requiredAnchors: [...requiredLongboxNodes],
					optionalAnchors: [...optionalLongboxNodes],
					requiredComicNodes: [...requiredComicNodes],
					optionalComicNodes: [...optionalComicNodes],
					referenceComicMetricsMeters: { ...boxedComicReferenceMeters },
					proxyReferenceMeters: { ...boxDims },
					proxyGuideMeters,
				},
			},
		],
	};
}

export function createFixtureFromTemplate(key, existingFixtures = []) {
	const template = getTemplateByKey(key);
	const comicVariant = template.kind === "boxed-comic" ? getComicPrefabVariant(template.defaultVariantKey) : null;
	const longboxVariant = template.kind === "longbox" ? getLongboxPrefabVariant(template.defaultVariantKey) : null;
	const count = existingFixtures.filter((fixture) => fixture.templateKey === template.key).length + 1;
	const tableSupport = template.kind === "longbox" ? existingFixtures.find((fixture) => fixture.templateKey === "display-table") : null;
	const xOffset = (existingFixtures.length % 4) * 0.8 - 1.2;
	const zOffset = Math.floor(existingFixtures.length / 4) * 1.15 - 0.5;
	const basePosition = { ...template.defaults.position };
	const defaultScale = comicVariant
		? comicMetricsToScale(comicVariant.referenceComicMetricsMeters)
		: longboxVariant
			? longboxMetricsToScale(longboxVariant.referenceLongboxMetricsMeters)
		: { ...template.defaults.scale };

	if (tableSupport && template.kind === "longbox") {
		basePosition.x = tableSupport.transform.position.x;
		basePosition.z = tableSupport.transform.position.z;
		basePosition.y = roundNumber(tableSupport.transform.position.y + tableSupport.transform.scale.y + 0.04, 2);
	} else {
		basePosition.x += xOffset;
		basePosition.z += zOffset;
	}

	return {
		id: `${template.key}-${Date.now().toString(36)}-${count}`,
		templateKey: template.key,
		name: count > 1 ? `${template.label} ${String(count).padStart(2, "0")}` : template.label,
		transform: {
			position: basePosition,
			rotation: { ...template.defaults.rotation },
			scale: defaultScale,
		},
		material: template.defaults.material,
		contract: buildFixtureContract(template, comicVariant, longboxVariant),
		storage: template.kind === "longbox" ? createStorageState(template) : null,
		assetBinding: template.kind === "longbox" || template.kind === "boxed-comic" ? createDefaultFixtureAssetBinding() : null,
		variantKey: comicVariant?.key || longboxVariant?.key || null,
		surfaceMaterials: getDefaultFixtureSurfaces(template.key),
	};
}

export function hydrateScene(candidate, fallbackTitle = "KapowX Authoring Studio") {
	if (!candidate || typeof candidate !== "object") {
		return createDefaultScene(fallbackTitle);
	}

	if (candidate.version && candidate.version !== scenePackageVersion) {
		return createDefaultScene(fallbackTitle);
	}

	const defaults = createDefaultScene(fallbackTitle);
	const room = candidate.room && typeof candidate.room === "object" ? candidate.room : defaults.room;
	const fixtures = Array.isArray(candidate.fixtures) ? candidate.fixtures : [];
	const projectSettings = hydrateProjectSettings(candidate.projectSettings);

	return {
		version: typeof candidate.version === "string" ? candidate.version : scenePackageVersion,
		metadata: {
			sceneId: candidate.metadata?.sceneId || defaults.metadata.sceneId,
			title: candidate.metadata?.title || fallbackTitle,
			mode: "authoring",
			focus: "comic-longbox-phase0",
		},
		room: {
			width: roundNumber(clampNumber(room.width, 1, 30, defaults.room.width), 2),
			depth: roundNumber(clampNumber(room.depth, 1, 30, defaults.room.depth), 2),
			height: roundNumber(clampNumber(room.height, 1, 10, defaults.room.height), 2),
			surfaces: hydrateRoomSurfaces(room.surfaces),
		},
		projectSettings,
		runtimeBindings: hydrateRuntimeBindings(candidate.runtimeBindings),
		shopGeometryBinding: hydrateShopGeometryBinding(candidate.shopGeometryBinding),
		fixtures: fixtures.map((fixture, index) => hydrateFixture(fixture, index, projectSettings)),
		staticSlots: Array.isArray(candidate.staticSlots) ? candidate.staticSlots : [...STATIC_SLOTS],
	};
}

export function buildExportPayload(state) {
	return {
		version: scenePackageVersion,
		exportedAt: new Date().toISOString(),
		metadata: {
			sceneId: state.scene.metadata.sceneId,
			title: state.scene.metadata.title,
			mode: "authoring",
			focus: "comic-longbox-phase0",
		},
		room: {
			width: state.scene.room.width,
			depth: state.scene.room.depth,
			height: state.scene.room.height,
			surfaces: hydrateRoomSurfaces(state.scene.room.surfaces),
		},
		projectSettings: hydrateProjectSettings(state.scene.projectSettings),
		contracts: {
			gltfAssetBasis: { ...gltfAssetBasis },
			semanticMetadata: { ...kapowxSemanticMetadata },
			comic: {
				kind: "comic-boxed",
				requiredNodes: [...requiredComicNodes],
				optionalNodes: [...optionalComicNodes],
				variants: getAvailableComicPrefabVariants(state.scene.projectSettings).map((item) => ({
					key: item.key,
					label: item.label,
					era: item.era,
					source: item.source,
					assetUrl: item.runtimeAssetUrl || item.assetUrl || "",
					runtimeAssetUrl: item.runtimeAssetUrl || item.assetUrl || "",
					creatorKitAssetUrl: item.creatorKitAssetUrl || "",
					assetValidation: hydrateComicAssetValidation(item.assetValidation),
					referenceComicMetricsMeters: { ...item.referenceComicMetricsMeters },
					materialOverrides: hydrateComicMaterialSlots(item.materialOverrides, true),
				})),
			},
			longbox: {
				kind: "long-box",
				requiredNodes: [...requiredLongboxNodes],
				optionalNodes: [...optionalLongboxNodes],
				laneDirectionSource: "slot-anchor-delta",
				variants: getAvailableLongboxPrefabVariants(state.scene.projectSettings).map((item) => ({
					key: item.key,
					label: item.label,
					source: item.source,
					assetUrl: item.runtimeAssetUrl || item.assetUrl || "",
					runtimeAssetUrl: item.runtimeAssetUrl || item.assetUrl || "",
					assetValidation: hydrateLongboxAssetValidation(item.assetValidation),
					referenceLongboxMetricsMeters: { ...item.referenceLongboxMetricsMeters },
				})),
			},
		},
		runtimeBindings: {
			sharedComic: { ...state.scene.runtimeBindings.sharedComic },
		},
		shopGeometryBinding: state.scene.shopGeometryBinding ? { ...state.scene.shopGeometryBinding } : null,
		staticSlots: [...STATIC_SLOTS],
		fixtures: state.scene.fixtures.map((fixture) => {
			const template = getTemplateByKey(fixture.templateKey);

			return {
				id: fixture.id,
				templateKey: fixture.templateKey,
				variantKey: fixture.variantKey || fixture.libraryItemKey || null,
				name: fixture.name,
				kind: template.kind,
				material: fixture.material,
				contract: buildExportFixtureContract(fixture.contract),
				assetBinding: fixture.assetBinding ? { ...fixture.assetBinding } : null,
				instanceMaterialOverrides:
					template.kind === "boxed-comic"
						? hydrateComicInstanceMaterialOverrides(fixture.instanceMaterialOverrides)
						: null,
				surfaceMaterials: (template.kind === "table" || template.kind === "longbox")
					? hydrateFixtureSurfaces(fixture.surfaceMaterials, fixture.templateKey)
					: null,
				transform: {
					position: { ...fixture.transform.position },
					rotation: { ...fixture.transform.rotation },
					scale: { ...fixture.transform.scale },
				},
				storage:
					template.kind === "longbox"
						? {
								slotCount: fixture.storage.slotCount,
								logicalCapacity: fixture.storage.logicalCapacity,
								cycleProducts: fixture.storage.cycleProducts === true,
								sequenceStartProductId: fixture.storage.sequenceStartProductId,
								laneDirectionSource: fixture.storage.laneDirectionSource,
								requiredAnchors: [...fixture.storage.requiredAnchors],
								optionalAnchors: [...fixture.storage.optionalAnchors],
								requiredComicNodes: [...fixture.storage.requiredComicNodes],
								optionalComicNodes: [...fixture.storage.optionalComicNodes],
								referenceComicFormat: fixture.storage.referenceComicFormat,
								referenceComicMetricsMeters: { ...fixture.storage.referenceComicMetricsMeters },
								proxyReferenceMeters: { ...fixture.storage.proxyReferenceMeters },
								proxyGuideMeters: JSON.parse(JSON.stringify(fixture.storage.proxyGuideMeters)),
								assignments: fixture.storage.assignments.map((assignment, index) => ({
									slotIndex: index,
									slotId: `storage_slot_${String(index + 1).padStart(3, "0")}`,
									productId: assignment.productId,
								})),
							}
						: null,
			};
		}),
		validation: state.validation,
	};
}

export function createDefaultLongboxProxyGuideMeters(dimensions = longboxReferenceMeters) {
	const width = Math.max(0.12, Number(dimensions?.width) || longboxReferenceMeters.width);
	const height = Math.max(0.12, Number(dimensions?.height) || longboxReferenceMeters.height);
	const depth = Math.max(0.12, Number(dimensions?.depth) || longboxReferenceMeters.depth);
	const sideInset = 0.02;
	const baseClearance = 0.01;
	const topReserve = 0.08;
	const laneLength = Math.max(0.12, width - sideInset * 2);
	const laneBreadth = Math.max(0.08, depth - sideInset * 2);
	const laneHeight = Math.max(0.08, height - baseClearance - topReserve);
	const floorY = baseClearance;
	const laneOriginY = floorY + laneHeight * 0.5;
	const slotSpacing = Math.max(boxedComicReferenceMeters.thickness * 1.2, 0.006);
	const slotStart = -laneLength * 0.5 + Math.max(boxedComicReferenceMeters.thickness * 0.7, 0.016);
	const breadthOffset = Math.max(0.04, laneBreadth * 0.42);

	return {
		originAnchor: { x: 0, y: 0, z: 0 },
		frontPlacementAnchor: { x: 0, y: 0, z: depth * 0.5 },
		laneOrigin: { x: 0, y: laneOriginY, z: 0 },
		laneBounds: { x: laneLength, y: laneHeight, z: laneBreadth },
		slotOne: { x: slotStart, y: floorY, z: 0 },
		slotTwo: { x: slotStart + slotSpacing, y: floorY, z: 0 },
		heroExtract: { x: 0, y: laneOriginY + boxedComicReferenceMeters.height * 0.95, z: breadthOffset },
		lidRoot: { x: 0, y: height - 0.01, z: -depth * 0.5 + 0.01 },
		labelSurface: { x: 0, y: height * 0.5, z: depth * 0.5 - 0.006 },
		sliderSideAnchor: { x: width * 0.5 - 0.012, y: laneOriginY, z: 0 },
		cameraFocusAnchor: { x: 0, y: laneOriginY + boxedComicReferenceMeters.height * 0.6, z: 0.06 },
	};
}

export function resolveFixtureTransform(scene, fixtureId, proposedTransform) {
	const fixture = Array.isArray(scene?.fixtures)
		? scene.fixtures.find((candidate) => candidate.id === fixtureId)
		: null;

	if (!fixture) {
		return proposedTransform;
	}

	const template = getTemplateByKey(fixture.templateKey);
	const scaleBounds = getScaleBounds(template);
	const resolved = {
		position: {
			...proposedTransform.position,
			y: Math.max(0, Number(proposedTransform.position?.y) || 0),
		},
		rotation: {
			x: 0,
			y: Number(proposedTransform.rotation?.y) || 0,
			z: 0,
		},
		scale: {
			x: Math.max(scaleBounds.minX, Number(proposedTransform.scale?.x) || fixture.transform.scale.x),
			y: Math.max(scaleBounds.minY, Number(proposedTransform.scale?.y) || fixture.transform.scale.y),
			z: Math.max(scaleBounds.minZ, Number(proposedTransform.scale?.z) || fixture.transform.scale.z),
		},
	};

	if (template.category !== "storage") {
		return resolved;
	}

	const supports = scene.fixtures.filter(
		(candidate) =>
			candidate.id !== fixtureId && getTemplateByKey(candidate.templateKey).category === "support",
	);

	let nextTransform = resolved;
	let changed = true;
	let guard = 0;

	while (changed && guard < 4) {
		changed = false;
		guard += 1;

		for (const support of supports) {
			const adjusted = resolveStorageAgainstSupport(
				{ ...fixture, transform: nextTransform },
				support,
			);

			if (!transformsMatch(nextTransform, adjusted)) {
				nextTransform = adjusted;
				changed = true;
			}
		}
	}

	return nextTransform;
}

export function getLongboxProxyGuideDefinition() {
	const width = longboxReferenceMeters.width;
	const height = longboxReferenceMeters.height;
	const depth = longboxReferenceMeters.depth;
	const guideMeters = createDefaultLongboxProxyGuideMeters();
	const comic = {
		width: boxedComicReferenceMeters.width / depth,
		height: boxedComicReferenceMeters.height / height,
		thickness: boxedComicReferenceMeters.thickness / width,
	};

	return {
		laneDirectionSource: "slot-anchor-delta",
		laneCenter: {
			x: guideMeters.laneOrigin.x / width,
			y: guideMeters.laneOrigin.y / height - 0.5,
			z: guideMeters.laneOrigin.z / depth,
		},
		laneSize: {
			x: guideMeters.laneBounds.x / width,
			y: guideMeters.laneBounds.y / height,
			z: guideMeters.laneBounds.z / depth,
		},
		comic,
		slotOne: {
			x: guideMeters.slotOne.x / width,
			y: guideMeters.slotOne.y / height - 0.5,
			z: guideMeters.slotOne.z / depth,
		},
		slotTwo: {
			x: guideMeters.slotTwo.x / width,
			y: guideMeters.slotTwo.y / height - 0.5,
			z: guideMeters.slotTwo.z / depth,
		},
		heroExtract: {
			x: guideMeters.heroExtract.x / width,
			y: guideMeters.heroExtract.y / height - 0.5,
			z: guideMeters.heroExtract.z / depth,
		},
		lidRoot: {
			x: guideMeters.lidRoot.x / width,
			y: guideMeters.lidRoot.y / height - 0.5,
			z: guideMeters.lidRoot.z / depth,
		},
		labelSurface: {
			x: guideMeters.labelSurface.x / width,
			y: guideMeters.labelSurface.y / height - 0.5,
			z: guideMeters.labelSurface.z / depth,
		},
		originAnchor: {
			x: guideMeters.originAnchor.x / width,
			y: guideMeters.originAnchor.y / height - 0.5,
			z: guideMeters.originAnchor.z / depth,
		},
		frontPlacementAnchor: {
			x: guideMeters.frontPlacementAnchor.x / width,
			y: guideMeters.frontPlacementAnchor.y / height - 0.5,
			z: guideMeters.frontPlacementAnchor.z / depth,
		},
		sliderSideAnchor: {
			x: guideMeters.sliderSideAnchor.x / width,
			y: guideMeters.sliderSideAnchor.y / height - 0.5,
			z: guideMeters.sliderSideAnchor.z / depth,
		},
		cameraFocusAnchor: {
			x: guideMeters.cameraFocusAnchor.x / width,
			y: guideMeters.cameraFocusAnchor.y / height - 0.5,
			z: guideMeters.cameraFocusAnchor.z / depth,
		},
	};
}

export function computeValidation(scene, products = []) {
	const messages = [];
	const productMap = new Map(products.map((product) => [Number(product.id), product]));
	const tables = scene.fixtures.filter((fixture) => getTemplateByKey(fixture.templateKey).kind === "table");
	const longboxes = scene.fixtures.filter((fixture) => getTemplateByKey(fixture.templateKey).kind === "longbox");
	const boxedComics = scene.fixtures.filter((fixture) => getTemplateByKey(fixture.templateKey).kind === "boxed-comic");

	if (!scene.fixtures.length) {
		messages.push({
			id: "scene-empty",
			level: "warning",
			message: "Instantiate a table and long comic box from the library to begin Phase 0 testing.",
		});
	}

	if (!longboxes.length && !boxedComics.length) {
		messages.push({
			id: "scene-no-commerce-fixture",
			level: "warning",
			message: "Add a comic or long comic box to exercise the Phase 0 commerce asset pipeline.",
		});
	}

	scene.fixtures.forEach((fixture) => {
		const template = getTemplateByKey(fixture.templateKey);
		const { position, scale } = fixture.transform;
		const xLimit = scene.room.width * 0.5 - scale.x * 0.5;
		const zLimit = scene.room.depth * 0.5 - scale.z * 0.5;

		if (Math.abs(position.x) > xLimit + 0.01 || Math.abs(position.z) > zLimit + 0.01) {
			messages.push({
				id: `bounds-${fixture.id}`,
				fixtureId: fixture.id,
				level: "error",
				message: `${fixture.name} extends beyond the current room bounds.`,
			});
		}

		if (position.y < -0.01) {
			messages.push({
				id: `floor-${fixture.id}`,
				fixtureId: fixture.id,
				level: "error",
				message: `${fixture.name} drops below the authored floor plane.`,
			});
		}

		if (position.y + scale.y > scene.room.height + 0.01) {
			messages.push({
				id: `height-${fixture.id}`,
				fixtureId: fixture.id,
				level: "error",
				message: `${fixture.name} exceeds the authored room height.`,
			});
		}

		if (template.kind === "longbox") {
			validateLongboxFixture(fixture, tables, productMap, messages);
		}

		if (template.category === "storage") {
			validateStorageBehavior(fixture, scene.fixtures, messages);
		}
	});

	const counts = messages.reduce(
		(result, message) => {
			result[message.level] += 1;
			return result;
		},
		{ error: 0, warning: 0, success: 0 },
	);

	const summary = [];

	if (!counts.error) {
		summary.push({
			id: "summary-room",
			level: "success",
			message: "Room and instantiated proxies stay inside the authored envelope.",
		});
	}

	if ((longboxes.length || boxedComics.length) && !counts.error) {
		summary.push({
			id: "summary-commerce-assets",
			level: counts.warning ? "warning" : "success",
			message: counts.warning
				? "Commerce asset export is close, but review the remaining contract and imagery warnings."
				: "Commerce asset contract checks are clean for this test build.",
		});
	}

	if (!summary.length) {
		summary.push({
			id: "summary-review",
			level: counts.error ? "error" : "warning",
			message: counts.error
				? "Resolve blocking authoring contract issues before Blender handoff."
				: "Review remaining warnings before export.",
		});
	}

	return {
		counts,
		messages,
		summary,
		isValid: counts.error === 0,
	};
}

function hydrateFixture(candidate, index, projectSettings = null) {
	const template = getTemplateByKey(candidate?.templateKey);
	const candidateVariantKey =
		candidate?.variantKey ||
		candidate?.libraryItemKey ||
		candidate?.contract?.variantKey ||
		candidate?.contract?.libraryItemKey ||
		template.defaultVariantKey;
	const comicVariant = template.kind === "boxed-comic"
		? resolveComicPrefabVariant(projectSettings, candidateVariantKey)
		: null;
	const longboxVariant = template.kind === "longbox"
		? resolveLongboxPrefabVariant(projectSettings, candidateVariantKey)
		: null;
	const fallback = createFixtureFromTemplate(template.key, Array.from({ length: index }, () => ({ templateKey: template.key })));
	const position = candidate?.transform?.position ?? fallback.transform.position;
	const rotation = candidate?.transform?.rotation ?? fallback.transform.rotation;
	const scale = candidate?.transform?.scale ?? fallback.transform.scale;
	const scaleBounds = getScaleBounds(template);

	const hydrated = {
		id: candidate?.id || `${template.key}-${index + 1}`,
		templateKey: template.key,
		name: candidate?.name || fallback.name || template.label,
		transform: {
			position: {
				x: roundNumber(clampNumber(position.x, -20, 20, fallback.transform.position.x), 2),
				y: roundNumber(clampNumber(position.y, 0, 8, fallback.transform.position.y), 2),
				z: roundNumber(clampNumber(position.z, -20, 20, fallback.transform.position.z), 2),
			},
			rotation: {
				x: 0,
				y: roundNumber(clampNumber(rotation.y, -180, 180, fallback.transform.rotation.y), 1),
				z: 0,
			},
			scale: {
				x: roundNumber(clampNumber(scale.x, scaleBounds.minX, 8, fallback.transform.scale.x), template.kind === "boxed-comic" ? 4 : 2),
				y: roundNumber(clampNumber(scale.y, scaleBounds.minY, 4, fallback.transform.scale.y), template.kind === "boxed-comic" ? 4 : 2),
				z: roundNumber(clampNumber(scale.z, scaleBounds.minZ, 4, fallback.transform.scale.z), template.kind === "boxed-comic" ? 4 : 3),
			},
		},
		material: candidate?.material || fallback.material || template.defaults.material,
		contract: {
			...buildFixtureContract(template, comicVariant, longboxVariant),
			...(candidate?.contract || {}),
		},
		storage: template.kind === "longbox" ? hydrateStorage(candidate?.storage, template) : null,
		assetBinding:
			template.kind === "longbox" || template.kind === "boxed-comic"
				? hydrateFixtureAssetBinding(candidate?.assetBinding)
				: null,
		variantKey: comicVariant?.key || longboxVariant?.key || null,
		instanceMaterialOverrides:
			template.kind === "boxed-comic"
				? hydrateComicInstanceMaterialOverrides(candidate?.instanceMaterialOverrides)
				: null,
		surfaceMaterials: hydrateFixtureSurfaces(candidate?.surfaceMaterials, template.key),
	};

	if (template.kind === "boxed-comic") {
		hydrated.transform.scale = comicMetricsToScale(comicVariant.referenceComicMetricsMeters);
		hydrated.contract = {
			...hydrated.contract,
			variantKey: comicVariant.key,
			variantLabel: comicVariant.label,
			variantEra: comicVariant.era,
			variantSource: comicVariant.source,
			variantKind: comicVariant.kind,
			variantExportPolicy: comicVariant.exportPolicy,
			prefabEnvelopeMeters: { ...comicVariant.referenceComicMetricsMeters },
			referenceComicMetricsMeters: { ...comicVariant.referenceComicMetricsMeters },
		};
	}

	if (template.kind === "longbox") {
		hydrated.contract = {
			...hydrated.contract,
			prefabKey: "longbox",
			prefabLabel: "Long Comic Box",
			variantKey: longboxVariant.key,
			variantLabel: longboxVariant.label,
			variantSource: longboxVariant.source,
			variantKind: longboxVariant.kind,
			variantExportPolicy: longboxVariant.exportPolicy,
			prefabEnvelopeMeters: { ...longboxVariant.referenceLongboxMetricsMeters },
			referenceLongboxMetricsMeters: { ...longboxVariant.referenceLongboxMetricsMeters },
		};
	}

	return hydrated;
}

function createDefaultRuntimeBindings() {
	return {
		sharedComic: {
			assetUrl: "",
			assetFormat: "glb",
		},
	};
}

function hydrateRuntimeBindings(candidate) {
	return {
		sharedComic: {
			...createDefaultRuntimeBindings().sharedComic,
			...(candidate?.sharedComic || {}),
		},
	};
}

function createDefaultFixtureAssetBinding() {
	return {
		assetUrl: "",
		assetFormat: "glb",
		productId: null,
	};
}

function hydrateFixtureAssetBinding(candidate) {
	return {
		...createDefaultFixtureAssetBinding(),
		...(candidate || {}),
	};
}

export function hydrateComicInstanceMaterialOverrides(candidate) {
	const source = candidate && typeof candidate === "object" ? candidate : {};
	const slots = source.slots && typeof source.slots === "object" ? source.slots : {};

	return {
		enabled: Boolean(source.enabled),
		slots: Object.fromEntries(
			comicMaterialSlotKeys.map((slotKey) => {
				const fallback = defaultComicInstanceMaterialOverrides.slots[slotKey];
				const slot = slots[slotKey] || {};

				return [
					slotKey,
					{
						mode: normalizeInstanceMaterialMode(slot.mode || fallback.mode),
						color: normalizeHexColor(slot.color || fallback.color),
					},
				];
			}),
		),
	};
}

function buildFixtureContract(template, comicVariant = null, longboxVariant = null) {
	if (template.kind === "table") {
		return {
			kind: "table",
			anchor: "floor",
			behaviorClass: "support",
			supports: ["long-comic-box", "boxed-comic-proxy"],
			supportSurfaces: ["top"],
		};
	}

	if (template.kind === "boxed-comic") {
		const variant = comicVariant || getComicPrefabVariant(template.defaultVariantKey);
		return {
			kind: "comic-boxed",
			prefabKey: template.prefabKey || "comic",
			prefabLabel: "Comic",
			anchor: "floor-or-support",
			behaviorClass: "storage",
			gltfAssetBasis: { ...gltfAssetBasis },
			semanticMetadata: { ...kapowxSemanticMetadata },
			requiredComicNodes: [...requiredComicNodes],
			optionalComicNodes: [...optionalComicNodes],
			variantKey: variant.key,
			variantLabel: variant.label,
			variantEra: variant.era,
			variantSource: variant.source,
			variantKind: variant.kind,
			variantExportPolicy: variant.exportPolicy,
			prefabEnvelopeMeters: { ...variant.referenceComicMetricsMeters },
			referenceComicMetricsMeters: { ...variant.referenceComicMetricsMeters },
		};
	}

	if (template.kind === "longbox") {
		const variant = longboxVariant || getLongboxPrefabVariant(template.defaultVariantKey);
		return {
			kind: "long-box",
			prefabKey: template.prefabKey || "longbox",
			prefabLabel: "Long Comic Box",
			anchor: "support-or-floor",
			behaviorClass: "storage",
			gltfAssetBasis: { ...gltfAssetBasis },
			semanticMetadata: { ...kapowxSemanticMetadata },
			laneDirectionSource: "slot-anchor-delta",
			// ASK-107 A3 / ASK-124 A8 — `support-interior` DELETED, not ratified. It was never a
			// receiver: a receiver is a SURFACE you attach a sticky face to, while a cabinet interior
			// is a CONTAINER you place products INTO — the socket/slot mechanism (D-043), a different
			// contract with different geometry. This was the last site still EMITTING the deleted word
			// (`proxy-export.js` carries `contract` verbatim into exported artifacts, so it was being
			// written into files, not merely held in memory). The ratified vocabulary is final at six
			// and lives in Content's `catalogue-tag-registry.json`; `engine/manifest-schema.js` and
			// `ws3/_attachment.js` mirror it.
			placementRules: ["floor", "support-top"],
			requiredAnchors: [...requiredLongboxNodes],
			optionalAnchors: [...optionalLongboxNodes],
			requiredComicNodes: [...requiredComicNodes],
			optionalComicNodes: [...optionalComicNodes],
			referenceComicFormat: "modern-standard",
			requiresComicAssetKind: "comic-boxed",
			requiresFixtureAssetKind: "long-box",
			variantKey: variant.key,
			variantLabel: variant.label,
			variantSource: variant.source,
			variantKind: variant.kind,
			variantExportPolicy: variant.exportPolicy,
			prefabEnvelopeMeters: { ...variant.referenceLongboxMetricsMeters },
			referenceLongboxMetricsMeters: { ...variant.referenceLongboxMetricsMeters },
		};
	}

	return {
		kind: template.kind,
		anchor: "floor",
		behaviorClass: template.category,
	};
}

function buildExportFixtureContract(contract = {}) {
	if (contract.kind === "comic-boxed") {
		return {
			kind: "comic-boxed",
			prefabKey: contract.prefabKey || "comic",
			variantKey: contract.variantKey || defaultComicVariantKey,
		};
	}

	if (contract.kind === "long-box") {
		return {
			kind: "long-box",
			prefabKey: contract.prefabKey || "longbox",
			variantKey: contract.variantKey || defaultLongboxVariantKey,
			laneDirectionSource: contract.laneDirectionSource || "slot-anchor-delta",
		};
	}

	return {
		kind: contract.kind || "fixture",
		anchor: contract.anchor || "floor",
		behaviorClass: contract.behaviorClass || "static",
	};
}

function hydrateProjectComicVariant(candidate, index) {
	const label = String(candidate?.label || "").trim();
	const runtimeAssetUrl = String(candidate?.runtimeAssetUrl || candidate?.assetUrl || "").trim();
	const creatorKitAssetUrl = String(candidate?.creatorKitAssetUrl || "").trim();

	if (!label) {
		return null;
	}

	return {
		key: String(candidate?.key || `comic-imported-${index + 1}`).trim() || `comic-imported-${index + 1}`,
		label,
		era: String(candidate?.era || "Creator Variant").trim() || "Creator Variant",
		source: String(candidate?.source || "Project").trim() || "Project",
		kind: String(candidate?.kind || "Imported Variant").trim() || "Imported Variant",
		exportPolicy:
			String(candidate?.exportPolicy || "Managed In Project Settings").trim() ||
			"Managed In Project Settings",
		assetUrl: runtimeAssetUrl,
		runtimeAssetUrl,
		creatorKitAssetUrl,
		assetValidation: hydrateComicAssetValidation(candidate?.assetValidation),
		referenceComicMetricsMeters: hydrateComicMetrics(candidate?.referenceComicMetricsMeters),
		materialOverrides: hydrateComicMaterialSlots(candidate?.materialOverrides, true),
	};
}

function hydrateProjectLongboxVariant(candidate, index) {
	const label = String(candidate?.label || "").trim();
	const runtimeAssetUrl = String(candidate?.runtimeAssetUrl || candidate?.assetUrl || "").trim();

	if (!label) {
		return null;
	}

	return {
		key: String(candidate?.key || `longbox-imported-${index + 1}`).trim() || `longbox-imported-${index + 1}`,
		label,
		source: String(candidate?.source || "Project").trim() || "Project",
		kind: String(candidate?.kind || "Imported Variant").trim() || "Imported Variant",
		exportPolicy:
			String(candidate?.exportPolicy || "Managed In Project Settings").trim() ||
			"Managed In Project Settings",
		assetUrl: runtimeAssetUrl,
		runtimeAssetUrl,
		assetValidation: hydrateLongboxAssetValidation(candidate?.assetValidation),
		referenceLongboxMetricsMeters: hydrateLongboxMetrics(candidate?.referenceLongboxMetricsMeters),
	};
}

function applyComicVariantOverride(variant, override = null) {
	if (!override) {
		return {
			...variant,
			assetUrl: variant.runtimeAssetUrl || variant.assetUrl || "",
			runtimeAssetUrl: variant.runtimeAssetUrl || variant.assetUrl || "",
			creatorKitAssetUrl: variant.creatorKitAssetUrl || "",
			assetValidation: hydrateComicAssetValidation(variant.assetValidation),
			referenceComicMetricsMeters: { ...variant.referenceComicMetricsMeters },
			materialOverrides: hydrateComicMaterialSlots(variant.materialOverrides, true),
		};
	}

	return {
		...variant,
		label: override.label || variant.label,
		era: override.era || variant.era,
		assetUrl: firstNonEmpty(override.runtimeAssetUrl, override.assetUrl, variant.runtimeAssetUrl, variant.assetUrl),
		runtimeAssetUrl: firstNonEmpty(override.runtimeAssetUrl, override.assetUrl, variant.runtimeAssetUrl, variant.assetUrl),
		creatorKitAssetUrl: firstNonEmpty(override.creatorKitAssetUrl, variant.creatorKitAssetUrl),
		assetValidation: hydrateComicAssetValidation(override.assetValidation || variant.assetValidation),
		referenceComicMetricsMeters: override.referenceComicMetricsMeters
			? hydrateComicMetrics({
					...variant.referenceComicMetricsMeters,
					...override.referenceComicMetricsMeters,
				})
			: { ...variant.referenceComicMetricsMeters },
		materialOverrides: hydrateComicMaterialSlots({
			...(variant.materialOverrides || {}),
			...(override.materialOverrides || {}),
		}, true),
	};
}

function applyLongboxVariantOverride(variant, override = null) {
	if (!override) {
		return {
			...variant,
			assetUrl: variant.runtimeAssetUrl || variant.assetUrl || "",
			runtimeAssetUrl: variant.runtimeAssetUrl || variant.assetUrl || "",
			assetValidation: hydrateLongboxAssetValidation(variant.assetValidation),
			referenceLongboxMetricsMeters: { ...variant.referenceLongboxMetricsMeters },
		};
	}

	return {
		...variant,
		label: override.label || variant.label,
		assetUrl: firstNonEmpty(override.runtimeAssetUrl, override.assetUrl, variant.runtimeAssetUrl, variant.assetUrl),
		runtimeAssetUrl: firstNonEmpty(override.runtimeAssetUrl, override.assetUrl, variant.runtimeAssetUrl, variant.assetUrl),
		assetValidation: hydrateLongboxAssetValidation(override.assetValidation || variant.assetValidation),
		referenceLongboxMetricsMeters: override.referenceLongboxMetricsMeters
			? hydrateLongboxMetrics({
					...variant.referenceLongboxMetricsMeters,
					...override.referenceLongboxMetricsMeters,
				})
			: { ...variant.referenceLongboxMetricsMeters },
	};
}

function hydrateVariantOverrides(candidate) {
	if (!candidate || typeof candidate !== "object") {
		return {};
	}

	return Object.fromEntries(
		Object.entries(candidate)
			.map(([key, override]) => [
				key,
				{
					runtimeAssetUrl: String(override?.runtimeAssetUrl || override?.assetUrl || "").trim(),
					creatorKitAssetUrl: String(override?.creatorKitAssetUrl || "").trim(),
					assetValidation: hydrateComicAssetValidation(override?.assetValidation),
					materialOverrides: hydrateComicMaterialSlots(override?.materialOverrides, true),
				},
			])
			.filter(([key]) => key),
	);
}

function hydrateLongboxVariantOverrides(candidate) {
	if (!candidate || typeof candidate !== "object") {
		return {};
	}

	return Object.fromEntries(
		Object.entries(candidate)
			.map(([key, override]) => [
				key,
				{
					runtimeAssetUrl: String(override?.runtimeAssetUrl || override?.assetUrl || "").trim(),
					assetValidation: hydrateLongboxAssetValidation(override?.assetValidation),
					referenceLongboxMetricsMeters: override?.referenceLongboxMetricsMeters
						? hydrateLongboxMetrics(override.referenceLongboxMetricsMeters)
						: null,
				},
			])
			.filter(([key]) => key),
	);
}

function hydrateComicAssetValidation(candidate) {
	const defaults = createDefaultComicAssetValidation();
	const status = String(candidate?.status || defaults.status).trim();

	return {
		version: comicAssetValidationVersion,
		status: ["not-checked", "checking", "valid", "warning", "invalid", "error"].includes(status)
			? status
			: defaults.status,
		checkedAt: String(candidate?.checkedAt || "").trim(),
		assetRole: String(candidate?.assetRole || defaults.assetRole).trim() || defaults.assetRole,
		summary: String(candidate?.summary || defaults.summary).trim() || defaults.summary,
		errors: Array.isArray(candidate?.errors) ? candidate.errors.map((item) => String(item)) : [],
		warnings: Array.isArray(candidate?.warnings) ? candidate.warnings.map((item) => String(item)) : [],
		details: Array.isArray(candidate?.details) ? candidate.details.map((item) => String(item)) : [],
	};
}

function hydrateLongboxAssetValidation(candidate) {
	const defaults = createDefaultLongboxAssetValidation();
	const status = String(candidate?.status || defaults.status).trim();

	return {
		version: longboxAssetValidationVersion,
		status: ["not-checked", "checking", "valid", "warning", "invalid", "error"].includes(status)
			? status
			: defaults.status,
		checkedAt: String(candidate?.checkedAt || "").trim(),
		assetRole: String(candidate?.assetRole || defaults.assetRole).trim() || defaults.assetRole,
		summary: String(candidate?.summary || defaults.summary).trim() || defaults.summary,
		errors: Array.isArray(candidate?.errors) ? candidate.errors.map((item) => String(item)) : [],
		warnings: Array.isArray(candidate?.warnings) ? candidate.warnings.map((item) => String(item)) : [],
		details: Array.isArray(candidate?.details) ? candidate.details.map((item) => String(item)) : [],
	};
}

function hydrateComicMaterialSlots(candidate, partial = false) {
	const source = candidate && typeof candidate === "object" ? candidate : {};
	const entries = comicMaterialSlotKeys
		.map((slotKey) => {
			const slot = source[slotKey];
			if (partial && !slot) {
				return null;
			}

			const fallback = defaultComicMaterialSlots[slotKey];
			return [
				slotKey,
				{
					source: normalizeMaterialSource(slot?.source || fallback.source),
					projectMode: normalizeProjectMaterialMode(slot?.projectMode || fallback.projectMode),
					color: normalizeHexColor(slot?.color || fallback.color),
					textureAssetId: String(slot?.textureAssetId || "").trim(),
					textureUrl: String(slot?.textureUrl || "").trim(),
				},
			];
		})
		.filter(Boolean);

	return Object.fromEntries(entries);
}

function hydrateProjectTextureAssets(candidate) {
	if (!Array.isArray(candidate)) {
		return [];
	}

	return candidate
		.map((asset, index) => {
			const id = String(asset?.id || `texture-${index + 1}`).trim();
			const url = String(asset?.url || "").trim();
			if (!id || !url) {
				return null;
			}

			return {
				id,
				label: String(asset?.label || id).trim() || id,
				url,
				source: String(asset?.source || "Project").trim() || "Project",
				scope: String(asset?.scope || "project").trim() || "project",
				slotKey: String(asset?.slotKey || "").trim(),
				createdAt: String(asset?.createdAt || "").trim(),
			};
		})
		.filter(Boolean);
}

function resolveProjectTextureForSlot(slot, textureAssets) {
	const textureAsset = textureAssets.find((asset) => asset.id === slot.textureAssetId);
	const textureUrl =
		slot.source === "project" && slot.projectMode === "texture"
			? textureAsset?.url || ""
			: slot.source === "project" && slot.projectMode === "external"
				? slot.textureUrl || ""
				: "";

	return {
		...slot,
		textureUrl,
		textureAsset,
	};
}

function normalizeMaterialSource(value) {
	const source = String(value || "").trim();
	if (["authored", "project", "product"].includes(source)) {
		return source;
	}

	return "project";
}

function normalizeProjectMaterialMode(value) {
	const mode = String(value || "").trim();
	if (["color", "texture", "external"].includes(mode)) {
		return mode;
	}

	return "color";
}

function normalizeInstanceMaterialMode(value) {
	const mode = String(value || "").trim();
	if (["default", "product", "color"].includes(mode)) {
		return mode;
	}

	return "default";
}

function normalizeHexColor(value) {
	const text = String(value || "").trim();

	if (/^#[0-9a-f]{6}$/i.test(text)) {
		return text;
	}

	return "#ffffff";
}

function firstNonEmpty(...values) {
	for (const value of values) {
		const text = String(value || "").trim();
		if (text) {
			return text;
		}
	}

	return "";
}

function parseGlbJson(arrayBuffer) {
	const view = new DataView(arrayBuffer);
	const magic = readAscii(arrayBuffer, 0, 4);

	if (magic !== "glTF") {
		throw new Error("Expected a binary glTF (.glb) asset.");
	}

	if (view.getUint32(4, true) !== 2) {
		throw new Error("Only GLB version 2 assets are supported.");
	}

	const jsonLength = view.getUint32(12, true);
	const jsonType = readAscii(arrayBuffer, 16, 4);

	if (jsonType !== "JSON") {
		throw new Error("The first GLB chunk is not JSON.");
	}

	return JSON.parse(new TextDecoder().decode(new Uint8Array(arrayBuffer, 20, jsonLength)));
}

function inspectComicAssetJson(gltf, { assetRole, expectedMetrics, checkedAt }) {
	const nodeNames = (gltf.nodes || []).map((node) => node?.name || "");
	const errors = [];
	const warnings = [];
	const details = [];
	const missing = requiredComicNodes.filter((name) => !nodeNames.includes(name));
	const guideNodes = nodeNames.filter((name) => name.startsWith("__guide__") || name.startsWith("__proxy__"));

	if (missing.length) {
		errors.push(`Missing required comic node${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}.`);
	}

	if (assetRole === "runtime" && guideNodes.length) {
		errors.push(`Runtime comic asset still contains ${guideNodes.length} guide/proxy scaffold node${guideNodes.length === 1 ? "" : "s"}.`);
	} else if (guideNodes.length) {
		warnings.push(`${guideNodes.length} guide/proxy scaffold node${guideNodes.length === 1 ? "" : "s"} found.`);
	}

	for (const name of requiredComicNodes) {
		const node = findGltfNode(gltf, name);
		if (!node) {
			continue;
		}

		if (name !== "comic_root" && !Number.isInteger(node.mesh)) {
			errors.push(`${name} must reference a mesh.`);
			continue;
		}

		if (Number.isInteger(node.mesh) && !meshHasTexcoords(gltf, node.mesh) && name !== "pages_block") {
			warnings.push(`${name} has no TEXCOORD_0 attribute, so product imagery may not map correctly.`);
		}
	}

	const front = getNodeBounds(gltf, "front_surface");
	const back = getNodeBounds(gltf, "back_surface");
	const spine = getNodeBounds(gltf, "spine_surface");
	const pages = getNodeBounds(gltf, "pages_block");

	if (front && back) {
		if (centerAxis(front, 2) <= centerAxis(back, 2)) {
			warnings.push("front_surface should sit in front of back_surface on local +Z.");
		}
	}

	if (spine && pages) {
		if (centerAxis(spine, 0) >= centerAxis(pages, 0)) {
			warnings.push("spine_surface should sit on the local -X edge of the comic.");
		}
	}

	if (pages) {
		const size = boundsSize(pages);
		const expected = expectedMetrics || boxedComicReferenceMeters;
		const widthDelta = relativeDelta(size.x, expected.width);
		const heightDelta = relativeDelta(size.y, expected.height);
		const thicknessDelta = relativeDelta(size.z, expected.thickness);

		details.push(`Detected pages_block size: ${size.x.toFixed(4)}m x ${size.y.toFixed(4)}m x ${size.z.toFixed(4)}m.`);

		if (widthDelta > 0.25 || heightDelta > 0.25) {
			warnings.push("pages_block dimensions differ materially from the selected comic variant metrics.");
		}

		if (thicknessDelta > 0.75) {
			warnings.push("pages_block thickness differs materially from the selected comic variant metrics.");
		}
	}

	if (!errors.length) {
		details.push("Required semantic nodes are present.");
	}

	const status = errors.length ? "invalid" : warnings.length ? "warning" : "valid";
	const summary =
		status === "valid"
			? "Runtime comic asset is valid"
			: status === "warning"
				? "Runtime comic asset has warnings"
				: "Runtime comic asset is invalid";

	return {
		version: comicAssetValidationVersion,
		status,
		checkedAt,
		assetRole,
		summary,
		errors,
		warnings,
		details,
	};
}

function inspectLongboxAssetJson(gltf, { assetRole, checkedAt }) {
	const nodeNames = (gltf.nodes || []).map((node) => node?.name || "");
	const errors = [];
	const warnings = [];
	const details = [];
	const missing = requiredLongboxNodes.filter((name) => !nodeNames.includes(name));
	const guideNodes = nodeNames.filter((name) => name.startsWith("__guide__") || name.startsWith("__proxy__"));

	if (missing.length) {
		errors.push(`Missing required longbox node${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}.`);
	}

	if (assetRole === "runtime" && guideNodes.length) {
		errors.push(`Runtime longbox asset still contains ${guideNodes.length} guide/proxy scaffold node${guideNodes.length === 1 ? "" : "s"}.`);
	} else if (guideNodes.length) {
		warnings.push(`${guideNodes.length} guide/proxy scaffold node${guideNodes.length === 1 ? "" : "s"} found.`);
	}

	const slotOne = findGltfNode(gltf, "slot_001_anchor");
	const slotTwo = findGltfNode(gltf, "slot_002_anchor");
	const laneBounds = getNodeBounds(gltf, "lane_bounds");

	if (slotOne && slotTwo) {
		const a = nodeTranslation(slotOne);
		const b = nodeTranslation(slotTwo);
		const distance = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
		details.push(`Detected slot spacing: ${distance.toFixed(4)}m.`);

		if (distance < 0.005) {
			warnings.push("slot_001_anchor and slot_002_anchor are almost coincident.");
		}

		const yShare = distance ? Math.abs(b[1] - a[1]) / distance : 0;
		if (yShare > 0.35) {
			warnings.push("Slot anchors do not define a stable horizontal lane progression.");
		}
	}

	if (laneBounds) {
		const size = boundsSize(laneBounds);
		const centerOffset = Math.hypot(
			centerAxis(laneBounds, 0),
			centerAxis(laneBounds, 1),
			centerAxis(laneBounds, 2),
		);
		details.push(`Detected lane_bounds size: ${size.x.toFixed(4)}m x ${size.y.toFixed(4)}m x ${size.z.toFixed(4)}m.`);

		if (centerOffset > 0.25) {
			warnings.push("lane_bounds geometry is far from its node origin. Apply mesh transforms so contract node transforms, not child geometry offsets, define placement.");
		}
	}

	if (!errors.length) {
		details.push("Required semantic nodes are present.");
	}

	const status = errors.length ? "invalid" : warnings.length ? "warning" : "valid";
	const summary =
		status === "valid"
			? "Runtime longbox asset is valid"
			: status === "warning"
				? "Runtime longbox asset has warnings"
				: "Runtime longbox asset is invalid";

	return {
		version: longboxAssetValidationVersion,
		status,
		checkedAt,
		assetRole,
		summary,
		errors,
		warnings,
		details,
	};
}

function findGltfNode(gltf, name) {
	return (gltf.nodes || []).find((node) => node?.name === name) || null;
}

function meshHasTexcoords(gltf, meshIndex) {
	const mesh = gltf.meshes?.[meshIndex];
	return Boolean(mesh?.primitives?.some((primitive) => primitive?.attributes?.TEXCOORD_0 !== undefined));
}

function getNodeBounds(gltf, nodeName) {
	const node = findGltfNode(gltf, nodeName);
	const mesh = Number.isInteger(node?.mesh) ? gltf.meshes?.[node.mesh] : null;

	if (!node || !mesh) {
		return null;
	}

	const scale = Array.isArray(node.scale) ? node.scale : [1, 1, 1];
	const translation = Array.isArray(node.translation) ? node.translation : [0, 0, 0];
	const min = [Infinity, Infinity, Infinity];
	const max = [-Infinity, -Infinity, -Infinity];

	mesh.primitives?.forEach((primitive) => {
		const accessor = gltf.accessors?.[primitive?.attributes?.POSITION];
		if (!accessor?.min || !accessor?.max) {
			return;
		}

		for (let axis = 0; axis < 3; axis += 1) {
			min[axis] = Math.min(min[axis], accessor.min[axis] * scale[axis] + translation[axis]);
			max[axis] = Math.max(max[axis], accessor.max[axis] * scale[axis] + translation[axis]);
		}
	});

	if (!Number.isFinite(min[0]) || !Number.isFinite(max[0])) {
		return null;
	}

	return { min, max };
}

function boundsSize(bounds) {
	return {
		x: bounds.max[0] - bounds.min[0],
		y: bounds.max[1] - bounds.min[1],
		z: bounds.max[2] - bounds.min[2],
	};
}

function centerAxis(bounds, axis) {
	return (bounds.min[axis] + bounds.max[axis]) * 0.5;
}

function nodeTranslation(node) {
	return Array.isArray(node?.translation) ? node.translation : [0, 0, 0];
}

function relativeDelta(actual, expected) {
	const baseline = Math.max(Math.abs(Number(expected) || 0), 0.0001);
	return Math.abs((Number(actual) || 0) - baseline) / baseline;
}

function readAscii(arrayBuffer, offset, length) {
	return String.fromCharCode(...new Uint8Array(arrayBuffer, offset, length));
}

function createSceneId() {
	if (globalThis.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}

	return `scene-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function comicMetricsToScale(metrics) {
	return {
		x: metrics.width,
		y: metrics.height,
		z: metrics.thickness,
	};
}

export function longboxMetricsToScale(metrics) {
	return {
		x: metrics.width,
		y: metrics.height,
		z: metrics.depth,
	};
}

function hydrateComicMetrics(candidate) {
	return {
		width: roundNumber(clampNumber(candidate?.width, 0.05, 0.4, boxedComicReferenceMeters.width), 4),
		height: roundNumber(clampNumber(candidate?.height, 0.05, 0.5, boxedComicReferenceMeters.height), 4),
		thickness: roundNumber(clampNumber(candidate?.thickness, 0.001, 0.02, boxedComicReferenceMeters.thickness), 4),
	};
}

function hydrateLongboxMetrics(candidate) {
	return {
		width: roundNumber(clampNumber(candidate?.width, 0.2, 2, longboxReferenceMeters.width), 4),
		height: roundNumber(clampNumber(candidate?.height, 0.1, 1, longboxReferenceMeters.height), 4),
		depth: roundNumber(clampNumber(candidate?.depth, 0.08, 1, longboxReferenceMeters.depth), 4),
	};
}

function createStorageState(template) {
	return {
		slotCount: template.storageDefaults.slotCount,
		logicalCapacity: template.storageDefaults.logicalCapacity,
		referenceComicFormat: template.storageDefaults.referenceComicFormat,
		sequenceStartProductId: null,
		assignments: [],
		requiredAnchors: [...template.storageDefaults.requiredAnchors],
		optionalAnchors: [...template.storageDefaults.optionalAnchors],
		laneDirectionSource: template.storageDefaults.laneDirectionSource,
		comicAssetKind: template.storageDefaults.comicAssetKind,
		fixtureAssetKind: template.storageDefaults.fixtureAssetKind,
		requiredComicNodes: [...template.storageDefaults.requiredComicNodes],
		optionalComicNodes: [...template.storageDefaults.optionalComicNodes],
		referenceComicMetricsMeters: { ...template.storageDefaults.referenceComicMetricsMeters },
		proxyReferenceMeters: { ...template.storageDefaults.proxyReferenceMeters },
		proxyGuideMeters: createDefaultLongboxProxyGuideMeters(template.storageDefaults.proxyReferenceMeters),
	};
}

function hydrateStorage(candidate, template) {
	const defaults = createStorageState(template);
	const slotCount = roundNumber(clampNumber(candidate?.slotCount, 1, 100, defaults.slotCount), 0);
	const rawAssignments = Array.isArray(candidate?.assignments) ? candidate.assignments : defaults.assignments;

	return {
		slotCount,
		sequenceStartProductId: candidate?.sequenceStartProductId ? Number(candidate.sequenceStartProductId) : null,
		logicalCapacity: Number(candidate?.logicalCapacity) || defaults.logicalCapacity,
		referenceComicFormat: candidate?.referenceComicFormat || defaults.referenceComicFormat,
		assignments: rawAssignments
			.slice(0, slotCount)
			.map((assignment, index) => {
				if (typeof assignment === "number") {
					return { slotIndex: index, productId: Number(assignment) };
				}

				return {
					slotIndex: Number.isFinite(assignment?.slotIndex) ? Number(assignment.slotIndex) : index,
					productId: assignment?.productId ? Number(assignment.productId) : null,
				};
			})
			.filter((assignment) => assignment.productId),
		requiredAnchors: Array.isArray(candidate?.requiredAnchors) && candidate.requiredAnchors.length
			? [...candidate.requiredAnchors]
			: defaults.requiredAnchors,
		optionalAnchors: Array.isArray(candidate?.optionalAnchors) ? [...candidate.optionalAnchors] : defaults.optionalAnchors,
		laneDirectionSource:
			candidate?.laneDirectionSource ||
			(candidate?.laneAxis ? "slot-anchor-delta" : defaults.laneDirectionSource),
		comicAssetKind: candidate?.comicAssetKind || defaults.comicAssetKind,
		fixtureAssetKind: candidate?.fixtureAssetKind || defaults.fixtureAssetKind,
		requiredComicNodes:
			Array.isArray(candidate?.requiredComicNodes) && candidate.requiredComicNodes.length
				? [...candidate.requiredComicNodes]
				: defaults.requiredComicNodes,
		optionalComicNodes:
			Array.isArray(candidate?.optionalComicNodes) ? [...candidate.optionalComicNodes] : defaults.optionalComicNodes,
		referenceComicMetricsMeters: candidate?.referenceComicMetricsMeters
			? { ...defaults.referenceComicMetricsMeters, ...candidate.referenceComicMetricsMeters }
			: defaults.referenceComicMetricsMeters,
		proxyReferenceMeters: candidate?.proxyReferenceMeters
			? { ...defaults.proxyReferenceMeters, ...candidate.proxyReferenceMeters }
			: defaults.proxyReferenceMeters,
		proxyGuideMeters: candidate?.proxyGuideMeters
			? mergeLongboxGuideMeters(defaults.proxyGuideMeters, candidate.proxyGuideMeters)
			: defaults.proxyGuideMeters,
		cycleProducts: candidate?.cycleProducts === true,
	};
}

function mergeLongboxGuideMeters(defaults, candidate) {
	return {
		originAnchor: { ...defaults.originAnchor, ...(candidate.originAnchor || {}) },
		frontPlacementAnchor: { ...defaults.frontPlacementAnchor, ...(candidate.frontPlacementAnchor || {}) },
		laneOrigin: { ...defaults.laneOrigin, ...(candidate.laneOrigin || {}) },
		laneBounds: { ...defaults.laneBounds, ...(candidate.laneBounds || {}) },
		slotOne: { ...defaults.slotOne, ...(candidate.slotOne || {}) },
		slotTwo: { ...defaults.slotTwo, ...(candidate.slotTwo || {}) },
		heroExtract: { ...defaults.heroExtract, ...(candidate.heroExtract || {}) },
		lidRoot: { ...defaults.lidRoot, ...(candidate.lidRoot || {}) },
		labelSurface: { ...defaults.labelSurface, ...(candidate.labelSurface || {}) },
		sliderSideAnchor: { ...defaults.sliderSideAnchor, ...(candidate.sliderSideAnchor || {}) },
		cameraFocusAnchor: { ...defaults.cameraFocusAnchor, ...(candidate.cameraFocusAnchor || {}) },
	};
}

function getScaleBounds(template) {
	if (template.kind === "boxed-comic") {
		return {
			minX: 0.05,
			minY: 0.05,
			minZ: 0.001,
		};
	}

	return {
		minX: 0.2,
		minY: 0.2,
		minZ: 0.05,
	};
}

function validateLongboxFixture(fixture, tables, productMap, messages) {
	const storage = fixture.storage;
	const assignments = Array.isArray(storage?.assignments) ? storage.assignments : [];
	const assignedProducts = assignments
		.map((assignment) => productMap.get(Number(assignment.productId)))
		.filter(Boolean);
	const hasTable = tables.length > 0;

	if (!storage || !storage.requiredAnchors?.length) {
		messages.push({
			id: `longbox-contract-${fixture.id}`,
			fixtureId: fixture.id,
			level: "error",
			message: `${fixture.name} is missing its long-box anchor contract metadata.`,
		});
	}

	if (!hasTable) {
		messages.push({
			id: `longbox-table-${fixture.id}`,
			fixtureId: fixture.id,
			level: "warning",
			message: `${fixture.name} has no table instantiated in the current room for support testing.`,
		});
	}

	if (!assignments.length) {
		messages.push({
			id: `longbox-empty-${fixture.id}`,
			fixtureId: fixture.id,
			level: "warning",
			message: `${fixture.name} has no sequential comic assignments yet.`,
		});
	}

	if (assignments.length > storage.slotCount) {
		messages.push({
			id: `longbox-overflow-${fixture.id}`,
			fixtureId: fixture.id,
			level: "error",
			message: `${fixture.name} contains more assigned comics than authored slot capacity.`,
		});
	}

	const missingBack = assignedProducts.filter((product) => !product.backImage).length;

	if (missingBack) {
		messages.push({
			id: `longbox-back-${fixture.id}`,
			fixtureId: fixture.id,
			level: "warning",
			message: `${missingBack} assigned comic ${missingBack === 1 ? "is" : "are"} missing back-cover imagery.`,
		});
	}

	if (storage.sequenceStartProductId && !productMap.has(Number(storage.sequenceStartProductId))) {
		messages.push({
			id: `longbox-sequence-${fixture.id}`,
			fixtureId: fixture.id,
			level: "warning",
			message: `${fixture.name} points at a sequence-start product that is not available in the current Woo catalog.`,
		});
	}
}

function validateStorageBehavior(fixture, allFixtures, messages) {
	const supports = allFixtures.filter(
		(candidate) =>
			candidate.id !== fixture.id && getTemplateByKey(candidate.templateKey).category === "support",
	);

	for (const support of supports) {
		if (!storageIntersectsSupport(fixture, support)) {
			continue;
		}

		messages.push({
			id: `behavior-${fixture.id}-${support.id}`,
			fixtureId: fixture.id,
			level: "error",
			message: `${fixture.name} intersects ${support.name}. Storage fixtures may rest on support surfaces, but they cannot move through support volumes.`,
		});
	}
}

function resolveStorageAgainstSupport(storageFixture, supportFixture) {
	if (!storageIntersectsSupport(storageFixture, supportFixture)) {
		return storageFixture.transform;
	}

	const supportTop = supportFixture.transform.position.y + supportFixture.transform.scale.y;
	const storageBottom = storageFixture.transform.position.y;
	const candidate = cloneTransform(storageFixture.transform);

	if (storageBottom >= supportTop - 0.18) {
		candidate.position.y = roundNumber(supportTop, 2);
		return candidate;
	}

	const storageBounds = getFixtureBounds(storageFixture);
	const supportBounds = getFixtureBounds(supportFixture);
	const overlapX = getAxisOverlap(storageBounds.xMin, storageBounds.xMax, supportBounds.xMin, supportBounds.xMax);
	const overlapZ = getAxisOverlap(storageBounds.zMin, storageBounds.zMax, supportBounds.zMin, supportBounds.zMax);
	const separation = 0.02;

	if (overlapX <= overlapZ) {
		const direction = storageFixture.transform.position.x >= supportFixture.transform.position.x ? 1 : -1;
		candidate.position.x = roundNumber(
			candidate.position.x + direction * (overlapX + separation),
			2,
		);
		return candidate;
	}

	const direction = storageFixture.transform.position.z >= supportFixture.transform.position.z ? 1 : -1;
	candidate.position.z = roundNumber(
		candidate.position.z + direction * (overlapZ + separation),
		2,
	);
	return candidate;
}

function storageIntersectsSupport(storageFixture, supportFixture) {
	const storageBounds = getFixtureBounds(storageFixture);
	const supportBounds = getFixtureBounds(supportFixture);
	const overlapsX = getAxisOverlap(storageBounds.xMin, storageBounds.xMax, supportBounds.xMin, supportBounds.xMax) > 0;
	const overlapsZ = getAxisOverlap(storageBounds.zMin, storageBounds.zMax, supportBounds.zMin, supportBounds.zMax) > 0;
	const storageBottom = storageFixture.transform.position.y;
	const storageTop = storageFixture.transform.position.y + storageFixture.transform.scale.y;
	const supportBottom = supportFixture.transform.position.y;
	const supportTop = supportFixture.transform.position.y + supportFixture.transform.scale.y;
	const overlapsY = storageBottom < supportTop - 0.01 && storageTop > supportBottom + 0.01;

	if (!overlapsX || !overlapsZ || !overlapsY) {
		return false;
	}

	const restingOnTop = storageBottom >= supportTop - 0.01;
	return !restingOnTop;
}

function getFixtureBounds(fixture) {
	const { position, rotation, scale } = fixture.transform;
	const angle = ((Number(rotation?.y) || 0) * Math.PI) / 180;
	const halfWidth = Math.abs(Math.cos(angle)) * (scale.x * 0.5) + Math.abs(Math.sin(angle)) * (scale.z * 0.5);
	const halfDepth = Math.abs(Math.sin(angle)) * (scale.x * 0.5) + Math.abs(Math.cos(angle)) * (scale.z * 0.5);

	return {
		xMin: position.x - halfWidth,
		xMax: position.x + halfWidth,
		zMin: position.z - halfDepth,
		zMax: position.z + halfDepth,
	};
}

function getAxisOverlap(minA, maxA, minB, maxB) {
	return Math.max(0, Math.min(maxA, maxB) - Math.max(minA, minB));
}

function cloneTransform(transform) {
	return {
		position: { ...transform.position },
		rotation: { ...transform.rotation },
		scale: { ...transform.scale },
	};
}

function transformsMatch(left, right) {
	return (
		left.position.x === right.position.x &&
		left.position.y === right.position.y &&
		left.position.z === right.position.z &&
		left.rotation.y === right.rotation.y &&
		left.scale.x === right.scale.x &&
		left.scale.y === right.scale.y &&
		left.scale.z === right.scale.z
	);
}
